import { create } from 'zustand';
import chatService from '../services/chatService';

export const chatStore = create((set, get) => ({
  // Estado
  conversas: [],
  conversaAtual: null,
  mensagens: [],
  usuariosOnline: new Set(),
  usuariosAtivos: [],
  digitando: null,
  conectado: false,
  carregando: false,
  erro: null,

  // Ações
  inicializar: async (userId) => {
    try {
      chatService.connect(userId);

      // Configurar listeners
      chatService.on('connected', async (data) => {
        set({ conectado: true, erro: null });
        
        // FALLBACK: Busca conversas via HTTP após 2 segundos se não receber via socket
        setTimeout(async () => {
          const { conversas } = get();
          if (conversas.length === 0) {
            try {
              const conversasHTTP = await chatService.buscarConversas(userId);
              set({ conversas: conversasHTTP });
            } catch (error) {
              console.error('❌ [ChatStore] Erro ao buscar conversas via HTTP:', error);
            }
          }
        }, 2000);
      });

      chatService.on('disconnected', (data) => {
        set({ conectado: false });
      });

      chatService.on('usuarios_ativos', (usuarios) => {
        set({ usuariosAtivos: usuarios });
        
        // Marca os usuários online
        const onlineIds = new Set(usuarios.filter(u => u.online).map(u => u.id));
        set({ usuariosOnline: onlineIds });
      });

      // Normaliza conversas recebidas do servidor para o shape esperado pelo frontend
      const normalizeConversas = (conversasRaw = []) => {
        return (conversasRaw || []).map((c) => {
          const conversaId = c.conversaId || c._id || null;
          const ultimaMensagem = c.ultimaMensagem || c.ultimaMensagem || '';
          const ultimaMensagemData = c.ultimaMensagemData || c.ultimaMensagemEm || c.ultimaMensagemEm || c.updatedAt || c.createdAt || null;
          // Se houver outroUsuario no payload, use-o; senão tente derivar pelos participantes
          let outroUsuario = c.outroUsuario;
          if (!outroUsuario) {
            // tenta inferir outro participante que não seja o userId
            const participante = (c.participantes || []).find((p) => p !== userId);
            if (participante) {
              outroUsuario = { id: participante };
            }
          }

          return {
            // preserve todos os dados originais para compatibilidade
            ...c,
            conversaId,
            ultimaMensagem,
            ultimaMensagemData,
            outroUsuario: outroUsuario || { id: null },
            mensagensNaoLidas: c.mensagensNaoLidas || 0,
          };
        });
      };

      chatService.on('conversas', (conversas) => {
        const conversasNorm = normalizeConversas(conversas);
        set({ conversas: conversasNorm, carregando: false });
      });

      chatService.on('nova_mensagem', (data) => {
        const { conversaAtual } = get();
        const msg = data.mensagem;

        // Se estiver na conversa aberta, adiciona e marca como lida
        if (conversaAtual && msg.conversaId === conversaAtual.conversaId) {
          set((state) => ({
            mensagens: [...state.mensagens, msg],
          }));

          // solicita ao servidor marcar como lida
          chatService.marcarLida(userId, msg._id);
        } else {
          // Incrementa contador de não lidas na conversa correspondente
          set((state) => {
            let encontrou = false;
            const conversas = state.conversas.map((c) => {
              if (c.conversaId === msg.conversaId) {
                encontrou = true;
                return { ...c, mensagensNaoLidas: (c.mensagensNaoLidas || 0) + 1, ultimaMensagem: msg.conteudo, ultimaMensagemData: msg.createdAt };
              }
              return c;
            });

            // Se não encontrou a conversa, opcionalmente adiciona uma nova entrada mínima
            if (!encontrou) {
              conversas.unshift({
                conversaId: msg.conversaId,
                outroUsuario: msg.remetente || { id: msg.remetenteId },
                ultimaMensagem: msg.conteudo,
                ultimaMensagemData: msg.createdAt,
                mensagensNaoLidas: 1,
              });
            }

            return { conversas };
          });
        }

        // Atualiza a lista de conversas via HTTP/Socket para manter consistência
        get().atualizarConversas(userId);
      });

      chatService.on('mensagem_enviada', (data) => {
        if (data.success) {
          set((state) => ({
            mensagens: [...state.mensagens, data.mensagem],
          }));
        }
      });

      chatService.on('historico', (data) => {
        set({ mensagens: data.mensagens || [], carregando: false });
      });

      chatService.on('usuario_online', (data) => {
        set((state) => {
          const usuariosOnline = new Set(state.usuariosOnline);
          usuariosOnline.add(data.usuarioId);
          return { usuariosOnline };
        });
      });

      chatService.on('usuario_offline', (data) => {
        set((state) => {
          const usuariosOnline = new Set(state.usuariosOnline);
          usuariosOnline.delete(data.usuarioId);
          return { usuariosOnline };
        });
      });

      chatService.on('usuario_digitando', (data) => {
        const { conversaAtual } = get();
        if (conversaAtual && data.usuarioId === conversaAtual.outroUsuario.id) {
          set({ digitando: data.digitando ? data.usuarioId : null });
        }
      });

      // Quando o servidor confirma que uma mensagem foi lida, atualizamos o estado local
      chatService.on('mensagem_lida', (data) => {
        try {
          const { mensagemId, conversaId, usuarioId: leitorId } = data || {};

          // Atualiza lista de mensagens atuais
          set((state) => {
            const mensagens = state.mensagens.map((m) =>
              m._id === mensagemId ? { ...m, lida: true } : m
            );

            return { mensagens };
          });

          // Atualiza conversas: decrementa contador de mensagensNaoLidas na conversa correta
          set((state) => {
            const conversas = state.conversas.map((c) => {
              if (c.conversaId === conversaId) {
                const novasNaoLidas = Math.max((c.mensagensNaoLidas || 0) - 1, 0);
                return { ...c, mensagensNaoLidas: novasNaoLidas };
              }
              return c;
            });

            return { conversas };
          });
        } catch (err) {
          console.error('❌ [ChatStore] Erro ao processar mensagem_lida:', err);
        }
      });

      chatService.on('error', (error) => {
        console.error('❌ [ChatStore] Erro:', error);
        set({ erro: error.message || 'Erro no chat' });
      });

      chatService.on('erro', (data) => {
        console.error('❌ [ChatStore] Erro do servidor:', data);
        set({ erro: data.message || 'Erro no chat' });
      });

    } catch (error) {
      console.error('❌ [ChatStore] Erro ao inicializar:', error);
      set({ erro: error.message, conectado: false });
    }
  },

  desconectar: () => {
    chatService.disconnect();
    set({
      conversas: [],
      conversaAtual: null,
      mensagens: [],
      usuariosOnline: new Set(),
      usuariosAtivos: [],
      digitando: null,
      conectado: false,
    });
  },

  selecionarConversa: (conversa, userId) => {
    set({ conversaAtual: conversa, carregando: true, mensagens: [] });
    // Marca todas como lidas no servidor e atualiza localmente o contador
    (async () => {
      try {
        await chatService.marcarTodasLidas(userId, conversa._id);
      } catch (err) {
        console.warn('⚠️ [ChatStore] Falha ao marcar todas lidas via HTTP:', err);
      }

      // Atualiza contador local imediatamente
      set((state) => {
        const conversas = state.conversas.map((c) =>
          c.conversaId === conversa.conversaId ? { ...c, mensagensNaoLidas: 0 } : c
        );
        return { conversas };
      });
    })();

    chatService.buscarHistorico(userId, conversa.outroUsuario.id, 1, 50);
  },

  enviarMensagem: (userId, destinatarioId, conteudo, tipo = 'texto') => {
    chatService.enviarMensagem(userId, {
      destinatarioId,
      conteudo,
      tipo,
    });
  },

  indicarDigitando: (userId, destinatarioId, digitando) => {
    chatService.digitando(userId, destinatarioId, digitando);
  },

  marcarComoLida: (userId, mensagemId) => {
    chatService.marcarLida(userId, mensagemId);
  },

  atualizarConversas: async (userId) => {
    try {
      const conversasRaw = await chatService.buscarConversas(userId);
      // normaliza resposta HTTP
      const conversas = (conversasRaw || []).map((c) => ({
        ...c,
        conversaId: c.conversaId || c._id || null,
        ultimaMensagem: c.ultimaMensagem || '',
        ultimaMensagemData: c.ultimaMensagemData || c.ultimaMensagemEm || c.updatedAt || c.createdAt || null,
        outroUsuario: c.outroUsuario || { id: null },
        mensagensNaoLidas: c.mensagensNaoLidas || 0,
      }));
      set({ conversas });
    } catch (error) {
      console.error('❌ [ChatStore] Erro ao atualizar conversas:', error);
    }
  },

  limparErro: () => {
    set({ erro: null });
  },

  // Getters
  getConversaAtual: () => get().conversaAtual,
  getMensagens: () => get().mensagens,
  getConversas: () => get().conversas,
  getUsuariosAtivos: () => get().usuariosAtivos,
  isUsuarioOnline: (userId) => get().usuariosOnline.has(userId),
  isConectado: () => {
    const estadoConectado = get().conectado;
    const socketConectado = chatService.isConnected();
    
    if (estadoConectado !== socketConectado) {
      set({ conectado: socketConectado });
      return socketConectado;
    }
    
    return estadoConectado;
  },
}));